import {
    Root,
    AccordionHeader,
    AccordionTrigger,
    AccordionContent,
    AccordionItem as Item,
} from '@radix-ui/react-accordion'
import React from 'react'
import styled, {css, keyframes} from 'styled-components'

import {Text} from '@/components/text'
import minusIcon from '@/images/icons/minus.svg'
import plusIcon from '@/images/icons/plus.svg'
import {focusStyles} from '@/styles/utils'
import mq from '@/utils/media-query'

import type {AccordionContentProps, AccordionTriggerProps} from '@radix-ui/react-accordion'
import type {ReactNode} from 'react'

export type Props = {
    collapsible?: boolean
    children: ReactNode
    dataTestId?: string
    onChange?: (value: number) => void
}

const ITEM_PREFIX = 'item-'

export const Accordion = ({children, collapsible, onChange, dataTestId}: Props) => (
    <Root
        type="single"
        collapsible={collapsible}
        data-testid={dataTestId}
        defaultValue={`${ITEM_PREFIX}0`}
        onValueChange={(value: string) => onChange && onChange(parseInt(value.split('-')[1]))}
    >
        {React.Children.map(children, (child, i) => (
            <AccordionItemWrapper key={`${ITEM_PREFIX}${i}`} value={`${ITEM_PREFIX}${i}`}>
                {child}
            </AccordionItemWrapper>
        ))}
    </Root>
)

type AccordionItemProps = {
    title: string
    children: ReactNode
}

export const AccordionItem = ({children, title}: AccordionItemProps) => (
    <div>
        <Header title={title} />
        <Content>{children}</Content>
    </div>
)

const Content = ({children, ...props}: AccordionContentProps) => (
    <AccordionContentWrapper {...props}>
        <ContentSpacing>{children}</ContentSpacing>
    </AccordionContentWrapper>
)

const Header = ({children, title, ...props}: AccordionTriggerProps) => (
    <AccordionHeader asChild>
        <Text as="h3" $variant="paragraph-large">
            <AccordionButton {...props}>
                <AccordionButtonInner>
                    {title}
                    <PlusIcon src={plusIcon} alt="" aria-hidden />
                    <MinusIcon src={minusIcon} alt="" aria-hidden />
                </AccordionButtonInner>
            </AccordionButton>
        </Text>
    </AccordionHeader>
)

const AccordionItemWrapper = styled(Item)`
    border-bottom: 1px solid ${({theme}) => theme.colors.border.secondary};
`

const AccordionButton = styled(AccordionTrigger)`
    width: 100%;
    padding: unset;
    padding-top: ${({theme}) => theme.spacings.s12};
    padding-bottom: ${({theme}) => theme.spacings.s12};
    color: inherit;
    text-align: unset;
    cursor: pointer;
    background: unset;
    border: unset;

    ${mq.min('sm')} {
        padding-top: ${({theme}) => theme.spacings.s16};
        padding-bottom: ${({theme}) => theme.spacings.s16};
    }

    &:focus-visible {
        ${focusStyles()}
        border-radius: ${({theme}) => theme.radii.arc4};
    }
`

const AccordionButtonInner = styled.span`
    display: grid;
    justify-content: space-between;
    grid-template-columns: 1fr auto;
    width: 100%;
    align-items: center;
    column-gap: ${({theme}) => theme.spacings.s12};

    ${mq.min('sm')} {
        column-gap: ${({theme}) => theme.spacings.s12};
    }
`

const slideUp = keyframes`
        from {
            height: var(--radix-accordion-content-height);
            opacity: 1;
        }

        to {
            opacity: 0;
            height: 0;
        }
`

const slideDown = keyframes`

        from {
            height: 0;
            opacity: 0;
        }

        to {
            height: var(--radix-accordion-content-height);
            opacity: 1;
        }
`

const AccordionContentWrapper = styled(AccordionContent)`
    overflow: hidden;

    &[data-state='open'] {
        animation: ${slideDown} ${({theme}) => theme.animationTimes.slow};
    }

    &[data-state='closed'] {
        animation: ${slideUp} ${({theme}) => theme.animationTimes.slow};
    }
`

const ContentSpacing = styled.div`
    padding-top: ${({theme}) => theme.spacings.s12};
    padding-bottom: ${({theme}) => theme.spacings.s12};

    ${mq.min('sm')} {
        padding-top: ${({theme}) => theme.spacings.s16};
        padding-bottom: ${({theme}) => theme.spacings.s16};
    }
`

const iconStyles = css`
    width: 24px;
    height: 24px;
    object-fit: contain;

    ${mq.min('sm')} {
        width: 32px;
        height: 32px;
    }
`

const PlusIcon = styled.img`
    ${iconStyles}

    ${AccordionButton}[data-state='open'] & {
        display: none;
    }
`

const MinusIcon = styled.img`
    ${iconStyles}

    ${AccordionButton}[data-state='closed'] & {
        display: none;
    }
`
